import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { taskApi } from '@/features/tasks/api/taskApi';
import type { TaskAssignmentPublicInfo } from '@/features/tasks/types/task.types';

const TaskResponsePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const initialAction = searchParams.get('action') as 'accept' | 'decline' | null;

  const [info, setInfo] = useState<TaskAssignmentPublicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(initialAction === 'decline');
  const [rejectionReason, setRejectionReason] = useState('');
  const [result, setResult] = useState<{ status: string } | null>(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        const data = await taskApi.getAssignmentByToken(token) as any;
        setInfo(data);
      } catch (err: any) {
        setError(err?.message || 'Invalid or expired link.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAccept = async () => {
    if (!token || submitting) return;
    try {
      setSubmitting(true);
      const res = await taskApi.respondToAssignment(token, 'accepted') as any;
      setResult({ status: res.status || 'accepted' });
    } catch (err: any) {
      setError(err?.message || 'Failed to accept task.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!token || submitting) return;
    try {
      setSubmitting(true);
      const res = await taskApi.respondToAssignment(token, 'rejected', rejectionReason || undefined) as any;
      setResult({ status: res.status || 'rejected' });
    } catch (err: any) {
      setError(err?.message || 'Failed to decline task.');
    } finally {
      setSubmitting(false);
    }
  };

  const priorityColors: Record<string, string> = {
    urgent: 'bg-red-100 text-red-800', high: 'bg-yellow-100 text-yellow-800',
    medium: 'bg-lex-accent-soft text-lex-accent', low: 'bg-gray-100 text-gray-800',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Link Unavailable</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Already responded or result just submitted
  if (result || (info && info.status !== 'pending')) {
    const status = result?.status || info?.status || 'unknown';
    const isAccepted = status === 'accepted';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isAccepted ? 'bg-green-100' : 'bg-red-100'}`}>
            {isAccepted ? (
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {isAccepted ? 'Task Accepted' : 'Task Declined'}
          </h2>
          <p className="text-gray-600">
            {isAccepted
              ? 'You have accepted this task assignment. The task owner has been notified.'
              : 'You have declined this task assignment. The task owner has been notified.'}
          </p>
        </div>
      </div>
    );
  }

  // Expired
  if (info?.expired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Link Expired</h2>
          <p className="text-gray-600">This task assignment link has expired. Please contact the task owner for a new notification.</p>
        </div>
      </div>
    );
  }

  // Show accept/decline form
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-lex-accent px-8 py-6">
          <h1 className="text-2xl font-bold text-lex-accent-contrast">Task Assignment</h1>
          <p className="text-lex-accent-soft mt-1">from {info?.assigner?.name}</p>
        </div>

        {/* Task details */}
        <div className="px-8 py-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">{info?.task?.title}</h2>

          {info?.task?.description && (
            <p className="text-gray-600 mb-4 whitespace-pre-wrap">{info.task.description}</p>
          )}

          <div className="flex flex-wrap gap-3 mb-6">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[info?.task?.priority || 'medium']}`}>
              {info?.task?.priority?.toUpperCase()}
            </span>
            {info?.task?.due_date && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Due: {new Date(info.task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
            {info?.task?.matter_number && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Engagement: {info.task.matter_number}
              </span>
            )}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          {showRejectForm ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for declining (optional)
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
                  placeholder="Explain why you're declining this task..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleReject}
                  disabled={submitting}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {submitting ? 'Submitting...' : 'Confirm Decline'}
                </button>
                <button
                  onClick={() => setShowRejectForm(false)}
                  className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Back
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={handleAccept}
                disabled={submitting}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Accepting...' : 'Accept Task'}
              </button>
              <button
                onClick={() => setShowRejectForm(true)}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
              >
                Decline Task
              </button>
            </div>
          )}
        </div>

        <div className="px-8 py-4 bg-gray-50 text-center">
          <p className="text-xs text-gray-400">WeBelieve — Business Workspace</p>
        </div>
      </div>
    </div>
  );
};

export default TaskResponsePage;
